// src/components/common/ErrorMessage.js
import React from 'react';
import { FaExclamationCircle, FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';
import './ErrorMessage.css';

/**
 * ErrorMessage - Standardized error/warning/info message component
 *
 * Provides consistent styling and user feedback for validation errors,
 * warnings, and informational messages across the application.
 *
 * @param {Object} props
 * @param {string} props.message - The message text to display (required)
 * @param {string} [props.type='error'] - Message type: 'error', 'warning', 'info'
 * @param {string} [props.variant='inline'] - Display variant: 'inline', 'block', 'banner'
 * @param {React.ReactNode} [props.icon] - Custom icon (overrides default type icon)
 * @param {boolean} [props.showIcon=true] - Whether to show an icon
 * @param {string} [props.className] - Additional CSS classes
 * @param {function} [props.onDismiss] - Optional dismiss callback for closeable messages
 *
 * @example
 * // Inline error (field-level validation)
 * <ErrorMessage
 *   message="Email is required"
 *   type="error"
 *   variant="inline"
 * />
 *
 * @example
 * // Block error (form-level validation)
 * <ErrorMessage
 *   message="Please fix the errors below before submitting"
 *   type="error"
 *   variant="block"
 * />
 *
 * @example
 * // Banner warning (page-level notification)
 * <ErrorMessage
 *   message="Your subscription will expire in 3 days"
 *   type="warning"
 *   variant="banner"
 * />
 */
const ErrorMessage = ({
  message,
  type = 'error',
  variant = 'inline',
  icon,
  showIcon = true,
  className = '',
  onDismiss
}) => {
  // Don't render if no message
  if (!message) return null;

  // Get default icon based on type
  const getDefaultIcon = () => {
    const iconProps = { className: 'error-message__icon-svg' };

    switch (type) {
      case 'error':
        return <FaExclamationCircle {...iconProps} />;
      case 'warning':
        return <FaExclamationTriangle {...iconProps} />;
      case 'info':
        return <FaInfoCircle {...iconProps} />;
      default:
        return <FaExclamationCircle {...iconProps} />;
    }
  };

  return (
    <div
      className={`error-message error-message--${type} error-message--${variant} ${className}`}
      role="alert"
      aria-live="polite"
    >
      {showIcon && (
        <div className="error-message__icon">
          {icon || getDefaultIcon()}
        </div>
      )}

      <div className="error-message__content">
        {message}
      </div>

      {onDismiss && (
        <button
          className="error-message__dismiss"
          onClick={onDismiss}
          aria-label="Dismiss message"
        >
          ×
        </button>
      )}
    </div>
  );
};

export default ErrorMessage;
